import { useState } from 'react';
import DataTable from 'react-data-table-component';
import { Helmet } from 'react-helmet';
import toast from 'react-hot-toast';

import Modal from './Modal';
import ConfirmPrompt from './ConfirmPrompt';
import TableLoader from './TableLoader';
import SectionHeader from './SectionHeader';
import DateRangePicker from '@wojtekmaj/react-daterange-picker';

import useAllocations from '../hooks/useAllocations';
import allocationService from '../services/allocationService';
import { formatDateToSydneyTimeZone } from '../utils/dateUtils';

const Allocations = () => {
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const [dateRange, setDateRange] = useState([null, null]);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);

  const [selectedAllocation, setSelectedAllocation] = useState(null);

  const [isDeletingAllocation, setIsDeletingAllocation] = useState(false);

  const [isConfirmDeleteModalOpen, setIsConfirmDeleteModalOpen] = useState(false);

  const { data, isLoading, mutate } = useAllocations({
    startDate: dateRange?.[0] || null,
    endDate: dateRange?.[1] || null,
    page,
    perPage,
    sortField,
    sortOrder
  });

  const allocations = data?.allocations;
  const allocationCount = data?.count;

  const handlePageChange = page => {
    setPage(page);
  };

  const handlePerRowsChange = async (newPerPage, page) => {
    setPerPage(newPerPage);
    setPage(page);
  };

  const handleDelete = (row) => {
    setSelectedAllocation(row);
    setIsConfirmDeleteModalOpen(true);
  }

  const deleteAllocation = async () => {
    try {
      setIsDeletingAllocation(true);

      await allocationService.deleteAllocation(selectedAllocation._id);

      mutate();

      toast.success('Allocation deleted');
    } catch (error) {
      toast.error('An error occurred. Please try again');
    } finally {
      setIsDeletingAllocation(false);
      setIsConfirmDeleteModalOpen(false);
    }

  }

  const handleSort = async (column, sortDirection) => {
    setSortField(column.sortField);
    setSortOrder(sortDirection === 'desc' ? 1 : -1);
  };

  const columns = [
    {
      name: 'Project',
      selector: row => row.project.name,
      sortable: true,
      sortField: 'project.name'
    },
    {
      name: 'Labourer',
      selector: row => row.labourer.user.fullName,
      sortable: true,
      sortField: 'labourer.user.fullName'
    },
    {
      name: 'Start date',
      selector: row => formatDateToSydneyTimeZone(row.startDate),
      sortable: true,
      sortField: 'startDate'
    },
    {
      name: 'End date',
      selector: row => formatDateToSydneyTimeZone(row.endDate),
      sortable: true,
      sortField: 'endDate'
    },
    {
      name: '',
      right: "true",
      cell: row => (
        <div className='flex items-center gap-2'>
          <button
            onClick={() => handleDelete(row)}
            className='py-1 px-2 text-xs font-semibold cursor-pointer rounded-md bg-red-600 text-white'
          >
            Delete
          </button>
        </div>
      )
    }
  ];

  return (
    <>
      <Helmet>
        <title>Allocations | ASE Group</title>
      </Helmet>

      <div className='border border-ase-blue rounded-lg pb-1'>
        <SectionHeader
          title='Allocations'
          actionsRight={(
            <div className='flex flex-col md:flex-row md:items-center gap-2 w-full'>
              <p className='text-white text-xs'>Period:</p>

              <DateRangePicker
                value={dateRange}
                onChange={setDateRange}
                format='dd/MM/y'
                className="filter-date-range"
              />
            </div>
          )}
        />

        <DataTable
          columns={columns}
          data={allocations}
          striped
          highlightOnHover
          pointerOnHover
          progressPending={isLoading}
          progressComponent={<TableLoader />}
          sortServer
          onSort={handleSort}
          pagination
          paginationServer
          paginationTotalRows={allocationCount}
          paginationRowsPerPageOptions={[10, 20, 50]}
          paginationPerPage={perPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />

        <Modal isOpen={isConfirmDeleteModalOpen}>
          <ConfirmPrompt
            message={`Are you sure you want to remove ${selectedAllocation?.labourer.user.fullName} from ${selectedAllocation?.project.name}?`}
            onConfirm={() => deleteAllocation(selectedAllocation)}
            onCancel={() => setIsConfirmDeleteModalOpen(null)}
            isLoading={isDeletingAllocation}
          />
        </Modal>
      </div>
    </>
  )
}

export default Allocations;
